/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import ReactAsyncSelect, { AsyncProps } from "react-select/async";
import SvgIcon from "./SvgIcon";

export type AsyncSelectProps<Option, IsMulti, Group> = AsyncProps<Option, IsMulti, Group> & {
  label?: string;
  info?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
};

const AsyncSelect = React.forwardRef(
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: AsyncSelectProps<Option, IsMulti, Group>,
    ref
  ) => {
    const { label, info, required, disabled, errorMessage, ...rest } = props;
    const invalid = !!errorMessage;

    return (
      <div className={`mb-3 ${invalid && "field-group--invalid"}`}>
        {label && <label className="label">{label}</label>}
        {required && <sup style={{ color: "red" }}>*</sup>}
        {info && (
          <span
            className="help"
            data-bs-toggle="tooltip"
            data-trigger="hover"
            data-placement="right"
            data-html="true"
            data-container="body"
            title={info}
          >
            <SvgIcon name="question-circle" className="ml-1 label" />
          </span>
        )}
        <ReactAsyncSelect
          isSearchable
          isClearable
          isDisabled={disabled}
          theme={(theme) => ({
            ...theme,
            borderRadius: 7,
            colors: {
              ...theme.colors,
              primary50: "#00897b50",
              primary25: "#00897b25",
              primary: "#00897b"
            },
            spacing: { baseUnit: 5.5 }
          })}
          ref={ref}
          {...rest}
        />
        {invalid && (
          <div className="field-helper">
            <label>{errorMessage}</label>
          </div>
        )}
      </div>
    );
  }
);

AsyncSelect.displayName = "AsyncSelect";

export default AsyncSelect;
